import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { waitlistAPI } from '../../services/api';
import mobileAppMockup from '../../assets/images/mobile-app-mockup.png';
import AppStoreBadge from '../common/AppStoreBadge';
import GooglePlayBadge from '../common/GooglePlayBadge';
import clickCollabTextImg from '../../assets/images/Artboard 4(2).png';

const AppComingSoonSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await waitlistAPI.subscribeToApp(email);
      toast.success("You're on the list! We'll notify you when the app launches.");
      setEmail('');
    } catch (error: any) {
      console.error('Waitlist subscription error:', error);
      if (error.response?.status === 409) {
           toast.success("You're already on the list!");
           setEmail('');
      } else {
           toast.error(error.response?.data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="relative py-24 bg-black overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          
          {/* Text Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex-1 text-center lg:text-left"
          >
            <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm">
              <span className="text-sm font-semibold text-purple-300 tracking-wide uppercase">Coming Soon</span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Click. Collab. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                Anywhere.
              </span>
            </h2>
            
            <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              The full ClickCollab experience in your pocket. Manage collaborations, 
              find talent, and showcase your portfolio on the go.
            </p>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0 mb-10">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-6 py-4 rounded-full bg-white/10 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white/5 transition-all backdrop-blur-sm"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-gray-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-white/20"
              >
                {isLoading ? 'Joining...' : 'Notify Me'}
              </button>
            </form>

            {/* Badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
              <p className="text-sm text-gray-400 uppercase tracking-[0.2em] font-medium">Available soon on</p>
              <div className="flex items-center gap-4">
                <AppStoreBadge className="h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity" />
                <GooglePlayBadge className="h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity" />
              </div>
            </div>
          </motion.div>

          {/* 3D Overlapping CSS Phone Mockups */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex-1 relative w-full flex items-center justify-center h-[400px] md:h-[500px]"
          >
            {/* Background glowing orb */}
            <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-3xl transform scale-75 animate-pulse" />

            <div className="relative w-full h-full max-w-md mx-auto perspective-1000 flex items-center justify-center">
              
              {/* Phone 1: Left Dashboard Mockup (Tilted Left) */}
              <motion.div 
                initial={{ rotateY: -10, rotateX: 5, x: -20, y: 30 }}
                animate={{ y: [30, 15, 30], rotateY: [-10, -5, -10] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute left-4 md:left-0 z-10 w-48 md:w-56 h-96 md:h-[450px] bg-slate-950 border border-white/10 rounded-[40px] shadow-2xl overflow-hidden transform-style-3d p-2 flex flex-col gap-3"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full z-20" />
                
                {/* Simulated Content */}
                <div className="flex-1 w-full h-full bg-gray-950 rounded-[32px] p-4 flex flex-col gap-4 overflow-hidden pt-7 border border-white/5">
                   <div className="text-white font-bold text-xs tracking-wide">Creative Portfolio</div>
                   {/* Grid portfolio */}
                   <div className="grid grid-cols-2 gap-2 flex-1">
                      <div className="bg-gradient-to-tr from-purple-500 to-indigo-500 rounded-xl aspect-[3/4]" />
                      <div className="bg-gradient-to-tr from-cyan-500 to-blue-500 rounded-xl aspect-[3/4] translate-y-4" />
                      <div className="bg-gradient-to-tr from-amber-500 to-orange-500 rounded-xl aspect-[3/4]" />
                      <div className="bg-gradient-to-tr from-rose-500 to-pink-500 rounded-xl aspect-[3/4] translate-y-4" />
                   </div>
                   {/* Bottom Navbar mock */}
                   <div className="h-10 bg-white/5 backdrop-blur-md rounded-full mt-2 flex justify-around items-center px-4">
                      <div className="w-2 h-2 rounded-full bg-white/80" />
                      <div className="w-2 h-2 rounded-full bg-white/30" />
                      <div className="w-2 h-2 rounded-full bg-white/30" />
                   </div>
                </div>
              </motion.div>

              {/* Phone 2: Right Splash screen Mockup with User's Logo (Elevated, Tilted Right) */}
              <motion.div 
                initial={{ rotateY: 15, rotateX: -5, x: 20, y: -20, rotateZ: 5 }}
                animate={{ y: [-20, -35, -20], rotateY: [15, 10, 15] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                whileHover={{ scale: 1.05, rotateY: 5, transition: { duration: 0.3 } }}
                className="absolute right-4 md:right-0 z-20 w-48 md:w-56 h-96 md:h-[450px] bg-gradient-to-b from-slate-900 to-black border-2 border-purple-500/30 rounded-[40px] shadow-[0_30px_60px_-12px_rgba(168,85,247,0.3)] overflow-hidden cursor-pointer p-1.5 flex flex-col items-center justify-center transform-style-3d backdrop-blur-xl"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full z-20" />

                {/* Splash Screen Core */}
                <div className="flex-1 w-full h-full bg-slate-950/80 rounded-[32px] flex flex-col items-center justify-center gap-4 relative overflow-hidden border border-white/5">
                   
                   {/* Overlay glowing background inside phone layout */}
                   <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-transparent to-transparent opacity-50" />
                   
                   {/* The Logo from public folder absolute */}
                   <motion.div
                     initial={{ scale: 0.8, opacity: 0 }}
                     whileInView={{ scale: 1, opacity: 1 }}
                     transition={{ delay: 0.5, duration: 0.6 }}
                     className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-2xl shadow-xl flex items-center justify-center p-0.5"
                   >
                     <img 
                       src="/Light DP (1).jpg" 
                       alt="ClickCollab Latest Logo" 
                       className="w-full h-full object-cover rounded-xl"
                     />
                   </motion.div>

                   {/* Brand Name Img Asset */}
                   <motion.div
                     initial={{ opacity: 0 }}
                     whileInView={{ opacity: 1 }}
                     transition={{ delay: 0.7, duration: 0.5 }}
                     className="h-6 md:h-7 mt-1 flex items-center justify-center"
                   >
                     <img 
                       src={clickCollabTextImg} 
                       alt="ClickCollab Name" 
                       className="h-full w-auto object-contain"
                     />
                   </motion.div>
                   
                   <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-1/4 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-pulse" />
                </div>
              </motion.div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AppComingSoonSection;
